import "./styles.scss";
import { GeneralContact } from "#root/queries/generalContacts";
import { useGeneralContacts, useDeleteGeneralContact } from '#root/queries/generalContacts';
import LoadingWrapper from '#root/components/LoadingWrapper';
import { Edit24Filled, Delete24Filled, NotepadPersonRegular } from '@fluentui/react-icons';
import { Field, Badge, Button, Dropdown, Option } from '@fluentui/react-components';
import Paginator from '#root/components/Paginator';
import { showToast } from '#root/components/GlobalToaster';
import InfiniteDropdown from '#root/components/InfiniteDropdown';
import { useInfiniteDropdown } from '#root/hooks/useInfiniteDropdown';
import { ROUTES } from '#root/routes/constants';
import { DialogBox } from '#root/components/DialogBox';
import { Incident, useIncidentById, useInfiniteIncidents } from "#root/queries/incidents";
import { useVTrackerSelectedIncident, useVAdminSelectedIncident } from "#root/queries/local";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LEVEL_OF_TREATMENT_ENUM, SERVICE_PROVIDED_ENUM } from "#root/utils/contacts";
import { Location, useInfiniteLocations, useLocationById } from "#root/queries/locations";
import { formatToLocalTime } from '#root/services/utils';
import { PERSON_ENUMS } from "#root/utils/persons";
import { Breadcrumbs } from '#root/components/Breadcrumbs';
import { getUnitEnumLabelByValue } from "#root/utils/enums";

interface FilterState {
    incidentId: string;
    serviceProvided: string;
    locationId: string;
    levelOfTreatment: string;
    age?: number;
}

const initialFilters: FilterState = {
    incidentId: '',
    serviceProvided: '',
    locationId: '',
    levelOfTreatment: '',
    age: undefined
};

const AllContacts: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isVAdmin = location.pathname.includes('/vadmin/');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [contactToDelete, setContactToDelete] = useState<GeneralContact | null>(null);

    const { selectedIncidentId: vTrackerIncidentId, setVTrackerSelectedIncidentId } = useVTrackerSelectedIncident();
    const { selectedIncidentId: vAdminIncidentId, setVAdminSelectedIncidentId } = useVAdminSelectedIncident();

    const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(isVAdmin ? vAdminIncidentId : vTrackerIncidentId);

    const [filters, setFilters] = useState<FilterState>(() => ({
        ...initialFilters,
        incidentId: selectedIncidentId || ''
    }));

    const {
        selectedData: selectedIncidentData,
        handleChange: handleIncidentChange,
        setFilter: setIncidentFilter,
        queryResult: incidentQueryResult
    } = useInfiniteDropdown<Incident>({
        initialId: selectedIncidentId || '',
        onChange: (id) => {
            setSelectedIncidentId(id);
            if (id) {
                if (isVAdmin) {
                    setVAdminSelectedIncidentId(id);
                } else {
                    setVTrackerSelectedIncidentId(id);
                }
            }
            setCurrentPage(1);
            setFilters(prev => ({ ...prev, incidentId: id || '' }));
        },
        useInfiniteQuery: useInfiniteIncidents,
        useItemById: useIncidentById
    });

    const {
        selectedData: selectedLocationData,
        handleChange: handleLocationChange,
        setFilter: setLocationFilter,
        queryResult: locationsQueryResult
    } = useInfiniteDropdown<Location>({
        onChange: (id) => {
            setCurrentPage(1);
            setFilters(prev => ({ ...prev, locationId: id || '' }));
        },
        useInfiniteQuery: useInfiniteLocations,
        useItemById: useLocationById
    });

    const {
        data: contactsResponse,
        isLoading,
        refetch
    } = useGeneralContacts({
        incidentId: filters.incidentId || undefined,
        serviceProvided: filters.serviceProvided || undefined,
        locationId: filters.locationId || undefined,
        levelOfTreatment: filters.levelOfTreatment || undefined,
        age: filters.age,
        skip: (currentPage - 1) * itemsPerPage,
        take: itemsPerPage
    });

    const deleteMutation = useDeleteGeneralContact();

    const totalCount = contactsResponse?.totalCount ?? 0;
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const contacts = contactsResponse?.items ?? [];

    const handleItemsPerPageChange = (newItemsPerPage: number): void => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        refetch();
    };

    const handleEdit = (contact: GeneralContact) => {
        navigate(
            isVAdmin
                ? `${ROUTES.VADMIN.EDIT_CONTACTS}?contactId=${contact.id}`
                : `${ROUTES.VTRACKER.EDIT_CONTACT}?contactId=${contact.id}`
        );
    };

    const handleDeleteClick = (contact: GeneralContact, e: React.MouseEvent) => {
        e.stopPropagation();
        setContactToDelete(contact);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (contactToDelete?.id) {
            try {
                await deleteMutation.mutateAsync({ id: contactToDelete.id });
                showToast('Contact deleted successfully', 'success');
                setIsDeleteDialogOpen(false);
                refetch();
            } catch (error) {
                showToast('Failed to delete contact', 'error');
            }
        }
    };

    const renderGeneralContactCard = (contact: GeneralContact) => {
        return (
            <div key={contact.id} className="large-card">
                <div className="large-card__content">
                    <div className="large-card__header">
                        <div className="large-card__info">
                            <div>
                                <span className="large-card__label">Created at: </span>
                                <span className="large-card__value">{formatToLocalTime(contact.createdDate, 'long')}</span>
                            </div>
                            <span className="large-card__title">{contact.unitId} | {contact.department ?? '-'}</span>
                        </div>
                        <div className="large-card__actions">
                            <Button
                                icon={<Edit24Filled />}
                                appearance="subtle"
                                onClick={() => handleEdit(contact)}
                            />
                            <Button
                                icon={<Delete24Filled />}
                                appearance="subtle"
                                onClick={(e) => handleDeleteClick(contact, e)}
                            />
                        </div>
                    </div>
                    <div className="large-card__details">
                        <div>
                            <span className="large-card__label">Person's First Name: </span>
                            <span className="large-card__value">{contact.firstName}</span>
                        </div>
                        <div>
                            <span className="large-card__label">Person's Last Name: </span>
                            <span className="large-card__value">{contact.lastName}</span>
                        </div>
                        <div>
                            <span className="large-card__label">Person's Gender: </span>
                            <span className="large-card__value">{contact.gender}</span>
                        </div>
                        <div>
                            <span className="large-card__label">Person's Estimated Age: </span>
                            <span className="large-card__value">{getUnitEnumLabelByValue(PERSON_ENUMS.AGE_RANGES, contact.age)}</span>
                        </div>
                        <div>
                            <span className="large-card__label">Incident Name: </span>
                            <span className="large-card__value">{contact.incidentName}</span>
                        </div>
                        <div>
                            <span className="large-card__label">Incident Address: </span>
                            <span className="large-card__value">{contact.incidentAddress}</span>
                        </div>
                        <div>
                            <span className="large-card__label">Service Provided: </span>
                            <span className="large-card__value">{contact.serviceProvided}</span>
                        </div>
                        <div>
                            <span className="large-card__label">Transporting Unit: </span>
                            <span className="large-card__value">{contact.transportingUnit}</span>
                        </div>
                        <div>
                            <span className="large-card__label">Person's Location: </span>
                            <span className="large-card__value">{contact.location}</span>
                        </div>
                        <div>
                            <span className="large-card__label">Location Address: </span>
                            <span className="large-card__value">{contact.locationAddress}</span>
                        </div>
                        <div>
                            <span className="large-card__label">Chief Complaint: </span>
                            <span className="large-card__value">{contact.chiefComplaint}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="all-contacts">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs />
                    </div>
                    <div className="emc-page-header__props">
                        <Badge size="extra-large" color="success" className="all-contacts__count">
                            General Contacts: {totalCount}
                        </Badge>
                    </div>
                </div>
                <div className="emc-page-header__top-row">
                    <div className="all-contacts__filters">
                        <Field label="Incident" className='all-contacts__incident'>
                            <InfiniteDropdown
                                value={filters.incidentId}
                                onChange={(_, data) => handleIncidentChange(data.value)}
                                onFilterChange={setIncidentFilter}
                                queryResult={incidentQueryResult}
                                itemToOption={(incident) => ({
                                    value: incident.id ?? '',
                                    text: incident.name ?? ''
                                })}
                                placeholder="All Incidents"
                                selectedItem={selectedIncidentData}
                                clearable
                            />
                        </Field>
                        <Field label="Service Provided" className='all-contacts__services'>
                            <Dropdown
                                clearable
                                placeholder="All Services"
                                value={filters.serviceProvided}
                                onOptionSelect={(_, data) => setFilters(prev => ({ ...prev, serviceProvided: data.optionValue || '' }))}>
                                {SERVICE_PROVIDED_ENUM.map(service => (
                                    <Option key={service} value={service}>
                                        {service}
                                    </Option>
                                ))}
                            </Dropdown>
                        </Field>
                        <Field label="Location" className='all-contacts__location'>
                            <InfiniteDropdown
                                value={filters.locationId}
                                onChange={(_, data) => handleLocationChange(data.value)}
                                onFilterChange={setLocationFilter}
                                queryResult={locationsQueryResult}
                                itemToOption={(location) => ({
                                    value: location.id ?? '',
                                    text: location.name ?? ''
                                })}
                                placeholder="All Locations"
                                selectedItem={selectedLocationData}
                                clearable
                            />
                        </Field>
                        <Field label="Age Range" className='all-contacts__age'>
                            <Dropdown
                                clearable
                                placeholder="All Ages"
                                value={getUnitEnumLabelByValue(PERSON_ENUMS.AGE_RANGES, filters.age)}
                                onOptionSelect={(_, data) => setFilters(prev => ({ ...prev, age: data.optionValue ? Number(data.optionValue) : undefined }))}
                            >
                                {PERSON_ENUMS.AGE_RANGES.map(range => (
                                    <Option key={range.value} value={range.value.toString()} text={getUnitEnumLabelByValue(PERSON_ENUMS.AGE_RANGES, range.value) ?? ''}>
                                        {getUnitEnumLabelByValue(PERSON_ENUMS.AGE_RANGES, range.value)}
                                    </Option>
                                ))}
                            </Dropdown>
                        </Field>
                        <Field label="Level of Treatment" className='all-contacts__treatment'>
                            <Dropdown
                                clearable
                                placeholder="All Levels of Treatment"
                                value={filters.levelOfTreatment}
                                onOptionSelect={(_, data) => setFilters(prev => ({ ...prev, levelOfTreatment: data.optionValue || '' }))}>
                                {LEVEL_OF_TREATMENT_ENUM.map(treatment => (
                                    <Option key={treatment} value={treatment}>
                                        {treatment}
                                    </Option>
                                ))}
                            </Dropdown>
                        </Field>
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>All Contacts <div className="divider" /></h2>
                </div>
            </div>

            <div className="all-contacts__content">
                <LoadingWrapper
                    isLoading={isLoading}
                    isEmpty={contacts.length === 0}
                    emptyMessage="No contacts found"
                    emptyIcon={<NotepadPersonRegular />}
                >
                    <div>
                        <div className="all-contacts__list">
                            {contacts.map((contact) => renderGeneralContactCard(contact))}
                            <Paginator
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                itemsPerPage={itemsPerPage}
                                onItemsPerPageChange={handleItemsPerPageChange}
                                totalItems={totalCount}
                                currentItems={(currentPage - 1) * itemsPerPage + 1}
                                showItemsPerPage={true}
                            />
                        </div>

                    </div>
                </LoadingWrapper>
            </div>

            <DialogBox
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
                title="Delete Contact"
                primaryButtonText="Delete"
                onPrimaryButtonClick={handleConfirmDelete}
                isPrimaryButtonLoading={deleteMutation.isPending}
            >
                {contactToDelete && (
                    <>Permanently delete a contact? You can't undo this action.</>
                )}
            </DialogBox>
        </div>
    );
};

export default AllContacts;